import { useMemo } from 'react'
import { DayCell } from './DayCell'
import type { CalendarTask, CalendarLog } from '@/types/calendar'
import type { WeatherDaily } from '@/types/weather'
import type { Overlays } from './OverlayToggles'

interface CalendarGridProps {
  month: string // "YYYY-MM"
  tasks: CalendarTask[]
  logs: CalendarLog[]
  weatherDaily: WeatherDaily[] | null
  overlays: Overlays
  selectedDate: string | null
  onSelectDate: (date: string) => void
}

const WEEKDAYS = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd']

function toDateKey(iso: string): string {
  return iso.slice(0, 10)
}

function buildCalendarDays(month: string): { date: string; dayNumber: number; isCurrentMonth: boolean }[] {
  const year = parseInt(month.slice(0, 4))
  const mon = parseInt(month.slice(5, 7))
  const firstOfMonth = new Date(year, mon - 1, 1)
  const lastOfMonth = new Date(year, mon, 0)

  // Monday = 1, Sunday = 0 → offset: (day + 6) % 7
  const startOffset = (firstOfMonth.getDay() + 6) % 7
  const gridStart = new Date(firstOfMonth)
  gridStart.setDate(gridStart.getDate() - startOffset)

  const endOffset = (7 - ((lastOfMonth.getDay() + 6) % 7 + 1)) % 7
  const gridEnd = new Date(lastOfMonth)
  gridEnd.setDate(gridEnd.getDate() + endOffset)

  const days: { date: string; dayNumber: number; isCurrentMonth: boolean }[] = []
  const cursor = new Date(gridStart)
  while (cursor <= gridEnd) {
    const y = cursor.getFullYear()
    const m = String(cursor.getMonth() + 1).padStart(2, '0')
    const d = String(cursor.getDate()).padStart(2, '0')
    days.push({
      date: `${y}-${m}-${d}`,
      dayNumber: cursor.getDate(),
      isCurrentMonth: cursor.getMonth() === mon - 1 && cursor.getFullYear() === year,
    })
    cursor.setDate(cursor.getDate() + 1)
  }
  return days
}

export function CalendarGrid({ month, tasks, logs, weatherDaily, overlays, selectedDate, onSelectDate }: CalendarGridProps) {
  const calendarDays = useMemo(() => buildCalendarDays(month), [month])

  const tasksByDate = useMemo(() => {
    const map = new Map<string, CalendarTask[]>()
    for (const task of tasks) {
      if (!task.next_due_at) continue
      const key = toDateKey(task.next_due_at)
      const list = map.get(key) || []
      list.push(task)
      map.set(key, list)
    }
    return map
  }, [tasks])

  const logsByDate = useMemo(() => {
    const map = new Map<string, CalendarLog[]>()
    for (const log of logs) {
      if (!log.performed_at) continue
      const key = toDateKey(log.performed_at)
      const list = map.get(key) || []
      list.push(log)
      map.set(key, list)
    }
    return map
  }, [logs])

  const weatherByDate = useMemo(() => {
    const map = new Map<string, WeatherDaily>()
    if (weatherDaily) {
      for (const day of weatherDaily) {
        map.set(day.date, day)
      }
    }
    return map
  }, [weatherDaily])

  const todayStr = new Date().toLocaleDateString('sv-SE') // YYYY-MM-DD format

  return (
    <div>
      <div className="grid grid-cols-7 mb-xs">
        {WEEKDAYS.map(d => (
          <div key={d} className="text-center font-label text-xs text-frame uppercase tracking-wider py-xs">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-frame/15 rounded-sm overflow-hidden">
        {calendarDays.map(day => (
          <DayCell
            key={day.date}
            date={day.date}
            dayNumber={day.dayNumber}
            isCurrentMonth={day.isCurrentMonth}
            isToday={day.date === todayStr}
            tasks={tasksByDate.get(day.date) || []}
            logs={logsByDate.get(day.date) || []}
            weather={weatherByDate.get(day.date)}
            overlays={overlays}
            isSelected={selectedDate === day.date}
            onClick={onSelectDate}
          />
        ))}
      </div>
    </div>
  )
}
